import { useState } from 'react'
import { OrderbookTableContentBaseProps, formatDateDDMMYYYY } from './OrderbookTableContentBase'
import { formatCompactNumber, getOrderTypeVariant } from '@/utils'
import { Badge } from '@/components/ui/badge'
import { Search, SlidersHorizontal } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import { OTCFilters } from '@/components/filters'
import { GatedValue } from '@/components/gate'

export const OrderbookTableContentMobile = ({
  data,
  isLoading = false,
  onRowClick,
  emptyMessage = 'No orders available',
  search = '',
  onSearchChange,
  type = '',
  onTypeChange,
  assetType = '',
  onAssetTypeChange,
  trancheSize = '',
  onTrancheSizeChange,
  sortBy = '',
  onSortByChange,
  sortDirectionFilter = '',
  onSortDirectionChange,
  onFilterButtonClick,
  financialsHidden = false
}: OrderbookTableContentBaseProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false)

  /**
   * See the note on `isGated` in the desktop twin: the caller's TIER decides,
   * with the row's own `financials_hidden` as the backstop. A row that is
   * merely missing an amount at `full` tier is missing data, not withheld
   * data, and `formatCompactNumber` already renders it as an em dash.
   */
  const isGated = (item: { financials_hidden?: boolean }) =>
    financialsHidden || item.financials_hidden === true

  const handleFilterClick = () => {
    if (onFilterButtonClick) {
      onFilterButtonClick()
    } else {
      setFiltersOpen(true)
    }
  }

  const getDealTypeColor = (dealType?: string) => {
    if (dealType === 'EQUITY') return '#3481ec'
    if (dealType === 'SAFT') return '#ac5bdb'
    if (dealType === 'LIQUID_TOKEN') return '#2ea66b'
    return 'rgba(45,41,35,0.72)'
  }

  return (
    <div className="flex flex-col">
      {/* Search and Filter - Figma node 344-4482 */}
      <div className="flex items-center gap-[8px] mb-3">
        {/* Search Input */}
        <div className="flex-1 flex items-center gap-[8px] bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(253,253,253,0.08)] px-[12px] py-[18px]">
          <Search size={16} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] shrink-0" />
          <input
            type="text"
            placeholder="Search by project name"
            value={search || ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] placeholder:text-[rgba(45,41,35,0.48)] dark:placeholder:text-[rgba(253,253,253,0.48)]"
          />
        </div>
        {/* Filter Button - 54x54px */}
        <button
          className="w-[54px] h-[54px] rounded-full bg-[rgba(255,255,255,0.62)] dark:bg-[rgba(253,253,253,0.1)] border border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center hover:bg-gray-50 dark:hover:bg-[rgba(253,253,253,0.15)] transition-colors shrink-0"
          onClick={handleFilterClick}
          aria-label="Filter options"
        >
          <SlidersHorizontal size={24} className="text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-4 rounded-lg animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && data.length === 0 && (
        <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
          {emptyMessage}
        </div>
      )}

      {/* Mobile cards list */}
      {!isLoading && data.length > 0 && (
        <div className="space-y-2">
          {data.map((item) => (
            <div
              key={item.order_id}
              className="bg-white dark:bg-[#1c1c1c] p-[12px] cursor-pointer"
              onClick={() => onRowClick?.(item)}
            >
              <div className="flex items-center gap-[12px]">
                <div className="relative shrink-0">
                  {item.logo ? (
                    <img
                      src={item.logo}
                      alt={item.project_name}
                      className="h-[36px] w-[36px] rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-logo.png'
                      }}
                    />
                  ) : (
                    <div className="h-[36px] w-[36px] rounded-full bg-[#f5f5f5] flex items-center justify-center">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {item.project_name?.charAt(0).toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-plus-jakarta font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] truncate">
                    {item.project_name}
                  </p>
                  <p
                    className="font-plus-jakarta font-semibold text-[14px] leading-[20px] truncate"
                    style={{ color: getDealTypeColor(item.deal_type) }}
                  >
                    {item.deal_type}
                  </p>
                </div>

                <Badge variant={getOrderTypeVariant(item.type)}>
                  {item.type}
                </Badge>
              </div>

              <div className="mt-[12px] grid grid-cols-4 gap-[8px]">
                <div>
                  <p className="font-plus-jakarta font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Amount
                  </p>
                  <p className="font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {/* Absent on a public-listing row — nothing was fetched. */}
                    {isGated(item) ? (
                      <GatedValue label="Offered amount" widthCh={4} />
                    ) : (
                      <>${formatCompactNumber(item.offered_amount)}</>
                    )}
                  </p>
                </div>
                <div>
                  <p className="font-plus-jakarta font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    FDV
                  </p>
                  <p className="font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {isGated(item) ? (
                      <GatedValue label="Fully diluted valuation" widthCh={4} />
                    ) : (
                      <>${formatCompactNumber(item.offered_fully_diluted_value)}</>
                    )}
                  </p>
                </div>
                <div>
                  <p className="font-plus-jakarta font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Round
                  </p>
                  <p className="font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {item.round || '－'}
                  </p>
                </div>
                <div>
                  <p className="font-plus-jakarta font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Data
                  </p>
                  <p className="font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {formatDateDDMMYYYY(item.date)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters Drawer */}
      <Drawer open={filtersOpen} onOpenChange={(open) => !open && setFiltersOpen(false)}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-center pb-4">
            <DrawerTitle className="text-[#2d2923] dark:text-[#fdfdfd] text-xl font-semibold">
              Filters & Sorting
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 overflow-y-auto">
            <OTCFilters
              search={search || ''}
              onSearchChange={onSearchChange || (() => {})}
              type={type || ''}
              onTypeChange={onTypeChange || (() => {})}
              assetType={assetType || ''}
              onAssetTypeChange={onAssetTypeChange || (() => {})}
              trancheSize={trancheSize || ''}
              onTrancheSizeChange={onTrancheSizeChange || (() => {})}
              sortBy={sortBy || ''}
              onSortByChange={onSortByChange || (() => {})}
              sortDirection={sortDirectionFilter || ''}
              onSortDirectionChange={onSortDirectionChange || (() => {})}
              financialsHidden={financialsHidden}
              className="w-full"
              layout="grid"
            />
          </div>
          <DrawerFooter className="border-t border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] pt-4">
            <DrawerClose asChild>
              <button className="w-full bg-[rgba(45,41,35,0.72)] dark:bg-[#fdfdfd] text-white dark:text-[#2d2923] hover:bg-[#2d2923] dark:hover:bg-[rgba(253,253,253,0.9)] h-12 text-base font-plus-jakarta font-medium rounded-md transition-colors">
                Apply Filters
              </button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
